import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../config/api';
import { clearStoredChat, claimStoredChat } from './chatStorage';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set axios default header if token exists
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(API_CONFIG.ENDPOINTS.VERIFY_TOKEN, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.valid) {
        // Token is valid, get user info from localStorage or fetch it
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(API_CONFIG.ENDPOINTS.LOGIN, {
        username,
        password
      });

      const { access_token, user: userData } = response.data;
      
      // Keep a stored AI chat only if it belongs to this user (#381)
      claimStoredChat(userData?.user_id);

      // Store token and user
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = async () => {
    // Call logout endpoint
    try {
      await axios.post(API_CONFIG.ENDPOINTS.LOGOUT);
    } catch (error) {
      // Ignore errors on logout
    }

    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Clear AI chat data, including the server conversation id (#381)
    clearStoredChat();

    // Clear axios header
    delete axios.defaults.headers.common['Authorization'];

    // Clear state
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};